// utils/filterHandlers.js
import React from 'react';
import { Input, Checkbox, DatePicker, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { determinePlanningSummary } from '@/utils/planningSummary';
import { renderNameFilters } from '../AttributeFilterUtils'; // Assuming this utility is external
import { extractActionOwnerNames } from '../AttributeFilterUtils'; // Assuming this utility is external
import DateFilterComponent from './DateFilterComponent';
const { RangePicker } = DatePicker; // Keep RangePicker if used in DateFilterComponent

export const handleActionCurrentFilter = (
  actionCurrentFilter,
  actionItem, // This is expected to be an array of action items
  attributeFilters,
  setAttributeFilters,
  searchTerm,
  setSearchTerm,
  handleDateRangeChange // Make sure this is used if DateFilterComponent needs it
) => {
  const updateAttributeFilters = (newValue) => {
    setAttributeFilters({
      ...attributeFilters,
      [actionCurrentFilter]: newValue,
    });
  };

  const handleClearFilters = () => {
    setAttributeFilters({});
    setSearchTerm(''); // **IMPORTANT**: Clear search term when filters are cleared
  };

  const clearButton = Object.keys(attributeFilters).length > 0 && (
    <Button
      icon={<CloseOutlined />}
      onClick={handleClearFilters}
      style={{ marginBottom: '10px', color: 'blue' }}
    >
      Clear Filters
    </Button>
  );

  // Helper to render checkbox list with search and select-all
  const renderCheckboxFilter = (
    uniqueValues,
    displayLimit = 0, // Default to 0, meaning no initial limit unless specified
    showSearch = true // Control search input visibility
  ) => {
    // Filter values based on the current search term
    const filteredBySearch = uniqueValues.filter(
      (item) => item && String(item).toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Determine which values to *display*
    const valuesToDisplay =
      searchTerm || displayLimit === 0
        ? filteredBySearch // If searching or no limit, show all filtered results
        : filteredBySearch.slice(0, displayLimit); // If no search and limit exists, show limited results

    // Check if all *unique values* (not just displayed ones) are selected
    const allUniqueValuesSelected =
      attributeFilters[actionCurrentFilter] === undefined ||
      attributeFilters[actionCurrentFilter]?.length === uniqueValues.length;

    return (
      <div style={{ marginTop: '10px' }}>
        {clearButton}
        {showSearch && (
          <Input
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm} // **IMPORTANT**: Make input a controlled component
            style={{ marginBottom: '10px' }}
          />
        )}
        <Checkbox
          indeterminate={
            attributeFilters[actionCurrentFilter]?.length > 0 &&
            attributeFilters[actionCurrentFilter]?.length < uniqueValues.length
          }
          checked={allUniqueValuesSelected}
          onChange={(e) => {
            // When "Select all" is clicked, it should select/deselect from *all* unique values
            updateAttributeFilters(e.target.checked ? uniqueValues : []);
          }}
        >
          (Select all)
        </Checkbox>
        {/* Render the values determined to be displayed */}
        {valuesToDisplay.map((item) => (
          <div key={item}>
            <Checkbox
              checked={
                attributeFilters[actionCurrentFilter] === undefined ||
                attributeFilters[actionCurrentFilter]?.includes(item)
              }
              onChange={(e) => {
                const currentFilters = attributeFilters[actionCurrentFilter] || [];
                updateAttributeFilters(
                  e.target.checked
                    ? [...currentFilters, item]
                    : currentFilters.filter((val) => val !== item)
                );
              }}
            >
              {String(item)}
            </Checkbox>
          </div>
        ))}

        {/* Display a message if there are more unique values than the display limit */}
        {displayLimit > 0 && !searchTerm && uniqueValues.length > displayLimit && (
          <p style={{ marginTop: '10px', fontSize: '0.85em', color: '#888' }}>
            Displaying first {displayLimit} items. Use search to find more.
          </p>
        )}
      </div>
    );
  };

  // --- Filter for 'owner' (assuming renderNameFilters handles its own search/display) ---
  if (actionCurrentFilter === 'owner') {
    const ownerNames = [
      ...new Set(
        actionItem
          .map((item) => {
            if (item.owner && item.owner.fullName) {
              return item.owner.fullName;
            }
            if (item.owner && item.owner.firstName && item.owner.lastName) {
              return `${item.owner.firstName} ${item.owner.lastName}`;
            }
            return null;
          })
          .filter(Boolean)
      ),
    ];

    return (
      <>
        {clearButton}
        {/* Assuming renderNameFilters handles its own search term */}
        {renderNameFilters(
          ownerNames,
          actionCurrentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }
  // --- Filter for 'collaborator' ---
  if (actionCurrentFilter === 'collaborator') {
    const uniqueCollaborators = [
      ...new Set(
        actionItem.flatMap((item) => {
          if (!Array.isArray(item.collaborator) || item.collaborator.length === 0) {
            return ['<not set>']; // Show '<not set>' if no collaborators
          }

          return item.collaborator.map((col) => {
            const first = col?.firstName || '';
            const last = col?.lastName || '';
            const full = `${first} ${last}`.trim();
            return full !== '' ? full : '<not set>';
          });
        })
      ),
    ].sort((a, b) => {
      if (a === '<not set>' && b !== '<not set>') return -1;
      if (a !== '<not set>' && b === '<not set>') return 1;
      return a.localeCompare(b);
    });

    return renderCheckboxFilter(uniqueCollaborators);
  }
  // --- Filter for 'Updated By' ---
  if (actionCurrentFilter === 'Updated By') {
    const uniqueUpdatedOwners = [
      ...new Set(actionItem.flatMap((item) => {
        const latestUpdate = (item.activity || [])
          .filter(ev => ev.event?.includes('is updated'))
          .sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate))[0];

        if (!latestUpdate || !latestUpdate.owner) {
          return ['<not set>']; // Return '<not set>' if no update or owner
        }

        const updater = latestUpdate.owner;
        return [`${updater.firstName || ''} ${updater.lastName || ''}`.trim()];
      })) // Removed .filter(name => name !== '') as '<not set>' should be included
    ].sort(); // Sort the names alphabetically

    return renderCheckboxFilter(uniqueUpdatedOwners); // No display limit, search enabled
  }

  // --- Filter for 'planningSummary' ---
  if (actionCurrentFilter === 'planningSummary') {
    const uniqueSummaries = [...new Set(actionItem.map((item) => determinePlanningSummary(item)))];
    return renderCheckboxFilter(uniqueSummaries); // No display limit, search enabled
  }
  // --- Filter for 'Accountable Workstream' ---
  // --- Filter for 'Accountable Workstream' ---
  if (actionCurrentFilter === 'Accountable Workstream') {
    const uniqueWorkstreams = [
      ...new Set(
        actionItem.map((item) => {
          const ownerRole = item.owner?.role?.name;
          const workstreamName =
            ownerRole === 'Workstream Sponsor'
              ? item.owner?.workstream?.workstreamName
              : item.owner?.subworkstream?.workstream?.workstreamName;

          // Always return '<not set>' if missing or empty
          return workstreamName && workstreamName.trim() !== ''
            ? workstreamName
            : '<not set>';
        })
      ),
    ].sort((a, b) => {
      if (a === '<not set>' && b !== '<not set>') return -1;
      if (a !== '<not set>' && b === '<not set>') return 1;
      return a.localeCompare(b);
    });

    return renderCheckboxFilter(uniqueWorkstreams);
  }

  // --- Filter for 'Accountable subworkstream' ---
  if (actionCurrentFilter === 'Accountable subworkstream') {
    const uniqueSubworkstreams = [
      ...new Set(
        actionItem.map((item) => {
          const subName = item.owner?.subworkstream?.subworkstreamName;
          return subName && subName.trim() !== ''
            ? subName
            : '<not set>'; // Always show "<not set>" for missing ones
        })
      ),
    ].sort((a, b) => {
      if (a === '<not set>' && b !== '<not set>') return -1;
      if (a !== '<not set>' && b === '<not set>') return 1;
      return a.localeCompare(b);
    });

    return renderCheckboxFilter(uniqueSubworkstreams);
  }
  if (actionCurrentFilter === 'createdBy') {
    const uniqueCreators = [
      ...new Set(actionItem.flatMap((item) => {
        if (!item.createdBy) {
          return ['<not set>']; // Return '<not set>' if createdBy object is missing
        }
        const creatorFullName = `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
        return [creatorFullName];
      })) // Removed .filter(name => name !== '') as '<not set>' should be included
    ].sort(); // Sort alphabetically

    return renderCheckboxFilter(uniqueCreators); // No display limit, search enabled
  }
  if (actionCurrentFilter === 'Created on' || actionCurrentFilter === 'Updated on') {
    return (
      <>
        {clearButton}
        <DateFilterComponent
          currentFilter={actionCurrentFilter}
          attributeFilters={attributeFilters}
          setAttributeFilters={setAttributeFilters}
          handleDateRangeChange={handleDateRangeChange} // Pass the handler if DateFilterComponent needs it
        />
      </>
    );
  }

  // --- Specific filters with initial display limit and search ---
  if (
    actionCurrentFilter === 'actionItemNumber' ||
    actionCurrentFilter === 'workstreamActionName'
  ) {
    const uniqueValues = [
      ...new Set(actionItem.map((i) => i?.[actionCurrentFilter]).filter(Boolean)),
    ];
    // Display first 60 values, allow search for the remaining
    return renderCheckboxFilter(uniqueValues, 60);
  }

  // --- Default fallback for other single-select checkbox fields (no limit by default) ---
  const uniqueValues = [
    ...new Set(actionItem.map((i) => i?.[actionCurrentFilter]).filter(Boolean)),
  ];
  return renderCheckboxFilter(uniqueValues);
};
